import React, { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {LayoutDashboard, FilePlus, FileText, CreditCard, DollarSign, Settings, BarChart3, LogOut, Menu, X, Home, Users, ChevronDown, CheckCircle, AlertTriangle, Trash2, FileCheck, Building2} from 'lucide-react'
import NovaGuia from "../components/NovaGuia"
import ListarGuias from "../components/ListarGuias"
import LixeiraGuias from "../components/LixeiraGuias"
import GerarPagamentos from "../components/GerarPagamentos"
import PagamentosGerados from "../components/PagamentosGerados"
import Administracao from "../components/Administracao"
import Relatorios from "../components/Relatorios"
import DashboardHome from "../components/DashboardHome"
import Aforamento from "../components/Aforamento"
import ListarAforamentos from "../components/ListarAforamentos"
import CadastrarFeirante from "../components/CadastrarFeirante"
import ListarFeirantes from "../components/ListarFeirantes"
import CadastrarVendedor from "../components/CadastrarVendedor"
import ListarVendedores from "../components/ListarVendedores"
import GerenciarTaxas from "../components/GerenciarTaxas"
import CadastrarCemiterio from "../components/CadastrarCemiterio"
import ListarCemiterios from "../components/ListarCemiterios"
import CadastroGeral from "../components/CadastroGeral"
import ListarCadastroGeral from "../components/ListarCadastroGeral"
import CadastrarEmpresa from "../components/CadastrarEmpresa"
import ListarEmpresas from "../components/ListarEmpresas"
import EmitirAlvara from "../components/EmitirAlvara"
import ListarAlvaras from "../components/ListarAlvaras"
import PagamentosConfirmados from "../components/PagamentosConfirmados"
import PagamentosExpirados from "../components/PagamentosExpirados"
import CadastrarBoleto from "../components/CadastrarBoleto"
import ListarBoletos from "../components/ListarBoletos"
import { useGuias } from "../context/GuiasContext"

interface DashboardProps {
  userRole: "admin" | "user"
  userName: string
  onLogout: () => void
}

type TabType =
  | "dashboard" | "nova-guia" | "listar-guias" | "lixeira-guias"
  | "gerar-pagamentos" | "pagamentos-gerados" | "pagamentos-confirmados" | "pagamentos-expirados"
  | "aforamento" | "listar-aforamentos" | "cadastrar-feirante" | "listar-feirantes"
  | "cadastrar-vendedor" | "listar-vendedores" | "cadastrar-cemiterio" | "listar-cemiterios"
  | "cadastro-geral" | "listar-cadastro-geral" | "cadastrar-boleto" | "listar-boletos"
  | "cadastrar-empresa" | "listar-empresas" | "emitir-alvara" | "listar-alvaras"
  | "administracao" | "relatorios"

export default function Dashboard({ userRole, userName, onLogout }: DashboardProps) {
  const { logoUrl } = useGuias()
  const [activeTab, setActiveTab] = useState<TabType>("dashboard")
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [editingGuiaId, setEditingGuiaId] = useState<string | undefined>(undefined)
  const [editingAforamentoId, setEditingAforamentoId] = useState<string | undefined>(undefined)
  const [viewingAforamentoId, setViewingAforamentoId] = useState<string | undefined>(undefined)
  const [editingFeiranteId, setEditingFeiranteId] = useState<string | undefined>(undefined)
  const [editingVendedorId, setEditingVendedorId] = useState<string | undefined>(undefined)
  const [editingCadastroGeralId, setEditingCadastroGeralId] = useState<string | undefined>(undefined)
  const [editingBoletoId, setEditingBoletoId] = useState<string | undefined>(undefined)
  const [editingEmpresaId, setEditingEmpresaId] = useState<string | undefined>(undefined)
  const [emitindoAlvaraEmpresaId, setEmitindoAlvaraEmpresaId] = useState<string | undefined>(undefined)
  const [editingAlvaraId, setEditingAlvaraId] = useState<string | undefined>(undefined)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  // Detecta tamanho da tela em tempo real
  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 1024
      setIsMobile(mobile)
      if (!mobile) setSidebarOpen(false)
    }
    check()
    window.addEventListener("resize", check)
    return () => window.removeEventListener("resize", check)
  }, [])

  const handleEditGuia = (guiaId: string) => { setEditingGuiaId(guiaId); setActiveTab("nova-guia") }
  const handleSaveComplete = () => { setEditingGuiaId(undefined); setActiveTab("listar-guias") }
  const handleEditAforamento = (id: string) => { setEditingAforamentoId(id); setViewingAforamentoId(undefined); setActiveTab("aforamento") }
  const handleViewAforamento = (id: string) => { setViewingAforamentoId(id); setEditingAforamentoId(id); setActiveTab("aforamento") }
  const handleSaveAforamentoComplete = () => { setEditingAforamentoId(undefined); setViewingAforamentoId(undefined); setActiveTab("listar-aforamentos") }
  const handleEditFeirante = (id: string) => { setEditingFeiranteId(id); setActiveTab("cadastrar-feirante") }
  const handleSaveFeiranteComplete = () => { setEditingFeiranteId(undefined); setActiveTab("listar-feirantes") }
  const handleEditVendedor = (id: string) => { setEditingVendedorId(id); setActiveTab("cadastrar-vendedor") }
  const handleSaveVendedorComplete = () => { setEditingVendedorId(undefined); setActiveTab("listar-vendedores") }
  const handleEditCadastroGeral = (id: string) => { setEditingCadastroGeralId(id); setActiveTab("cadastro-geral") }
  const handleSaveCadastroGeralComplete = () => { setEditingCadastroGeralId(undefined); setActiveTab("listar-cadastro-geral") }
  const handleEditBoleto = (id: string) => { setEditingBoletoId(id); setActiveTab("cadastrar-boleto") }
  const handleSaveBoletoComplete = () => { setEditingBoletoId(undefined); setActiveTab("listar-boletos") }
  const handleEditEmpresa = (id: string) => { setEditingEmpresaId(id); setActiveTab("cadastrar-empresa") }
  const handleSaveEmpresaComplete = () => { setEditingEmpresaId(undefined); setActiveTab("listar-empresas") }
  const handleEmitirAlvara = (empresaId: string) => { setEmitindoAlvaraEmpresaId(empresaId); setEditingAlvaraId(undefined); setActiveTab("emitir-alvara") }
  const handleEditAlvara = (alvaraId: string) => { setEditingAlvaraId(alvaraId); setEmitindoAlvaraEmpresaId(undefined); setActiveTab("emitir-alvara") }
  const handleSaveAlvaraComplete = () => { setEditingAlvaraId(undefined); setEmitindoAlvaraEmpresaId(undefined); setActiveTab("listar-alvaras") }
  const toggleDropdown = (groupId: string) => setOpenDropdown(openDropdown === groupId ? null : groupId)

  const handleNavigate = (tab: TabType) => {
    setActiveTab(tab)
    if (isMobile) setSidebarOpen(false)
  }

  const menuGroups = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, tab: "dashboard" as TabType },
    {
      id: "guias", label: "Guias", icon: FileText,
      items: [
        { id: "nova-guia" as TabType, label: "Nova Guia", icon: FilePlus },
        { id: "listar-guias" as TabType, label: "Listar Guias", icon: FileText },
        { id: "lixeira-guias" as TabType, label: "Lixeira de Guias", icon: Trash2 },
        { id: "cadastrar-cemiterio" as TabType, label: "Cadastrar Cemitério", icon: FilePlus },
        { id: "listar-cemiterios" as TabType, label: "Listar Cemitérios", icon: FileText },
      ]
    },
    {
      id: "pagamentos", label: "Pagamentos", icon: DollarSign,
      items: [
        { id: "gerar-pagamentos" as TabType, label: "Gerar Pagamentos", icon: CreditCard },
        { id: "pagamentos-gerados" as TabType, label: "Pagamentos Gerados", icon: DollarSign },
        { id: "pagamentos-confirmados" as TabType, label: "Pagamentos Confirmados", icon: CheckCircle },
        { id: "pagamentos-expirados" as TabType, label: "Pagamentos Expirados", icon: AlertTriangle },
      ]
    },
    {
      id: "aforamentos", label: "Aforamentos", icon: Home,
      items: [
        { id: "aforamento" as TabType, label: "Novo Aforamento", icon: FilePlus },
        { id: "listar-aforamentos" as TabType, label: "Listar Aforamentos", icon: FileText },
      ]
    },
    {
      id: "vendedores", label: "Vendedores / Feirantes", icon: Users,
      items: [
        { id: "cadastrar-vendedor" as TabType, label: "Cadastrar Vendedor", icon: FilePlus },
        { id: "listar-vendedores" as TabType, label: "Listar Vendedores", icon: Users },
        { id: "cadastrar-feirante" as TabType, label: "Cadastrar Feirante", icon: FilePlus },
        { id: "listar-feirantes" as TabType, label: "Listar Feirantes", icon: Users },
      ]
    },
    {
      id: "cadastro-geral", label: "Cadastro Geral", icon: Users,
      items: [
        { id: "cadastro-geral" as TabType, label: "Novo Cadastro", icon: FilePlus },
        { id: "listar-cadastro-geral" as TabType, label: "Listar Cadastros", icon: Users },
      ]
    },
    {
      id: "boletos", label: "Boletos", icon: FileText,
      items: [
        { id: "cadastrar-boleto" as TabType, label: "Cadastrar Boleto", icon: FilePlus },
        { id: "listar-boletos" as TabType, label: "Listar Boletos", icon: FileText },
      ]
    },
    {
      id: "alvaras", label: "Alvará de Funcionamento", icon: FileCheck,
      items: [
        { id: "cadastrar-empresa" as TabType, label: "Cadastrar Empresa", icon: Building2 },
        { id: "listar-empresas" as TabType, label: "Listar Empresas", icon: Building2 },
        { id: "emitir-alvara" as TabType, label: "Emitir Alvará", icon: FileCheck },
        { id: "listar-alvaras" as TabType, label: "Listar Alvarás", icon: FileCheck },
      ]
    },
    { id: "relatorios", label: "Relatórios", icon: BarChart3, tab: "relatorios" as TabType },
    { id: "administracao", label: "Administração", icon: Settings, tab: "administracao" as TabType, adminOnly: true },
  ]

  // Barra inferior mobile — atalhos mais usados
  const bottomNav = [
    { tab: "dashboard" as TabType, label: "Início", icon: LayoutDashboard },
    { tab: "nova-guia" as TabType, label: "Nova Guia", icon: FilePlus },
    { tab: "gerar-pagamentos" as TabType, label: "Pagamentos", icon: CreditCard },
    { tab: "listar-guias" as TabType, label: "Guias", icon: FileText },
    { tab: "menu" as TabType, label: "Menu", icon: Menu },
  ]

  const allTabs: { id: TabType; label: string }[] = [
    { id: "dashboard", label: "Dashboard" },
    { id: "nova-guia", label: "Nova Guia" },
    { id: "listar-guias", label: "Listar Guias" },
    { id: "lixeira-guias", label: "Lixeira de Guias" },
    { id: "gerar-pagamentos", label: "Gerar Pagamentos" },
    { id: "pagamentos-gerados", label: "Pagamentos Gerados" },
    { id: "pagamentos-confirmados", label: "Pagamentos Confirmados" },
    { id: "pagamentos-expirados", label: "Pagamentos Expirados" },
    { id: "aforamento", label: "Novo Aforamento" },
    { id: "listar-aforamentos", label: "Listar Aforamentos" },
    { id: "cadastrar-feirante", label: "Cadastrar Feirante" },
    { id: "listar-feirantes", label: "Listar Feirantes" },
    { id: "cadastrar-vendedor", label: "Cadastrar Vendedor" },
    { id: "listar-vendedores", label: "Listar Vendedores" },
    { id: "cadastrar-cemiterio", label: "Cadastrar Cemitério" },
    { id: "listar-cemiterios", label: "Listar Cemitérios" },
    { id: "cadastro-geral", label: "Cadastro Geral" },
    { id: "listar-cadastro-geral", label: "Listar Cadastros" },
    { id: "cadastrar-boleto", label: "Cadastrar Boleto" },
    { id: "listar-boletos", label: "Listar Boletos" },
    { id: "cadastrar-empresa", label: "Cadastrar Empresa" },
    { id: "listar-empresas", label: "Listar Empresas" },
    { id: "emitir-alvara", label: "Emitir Alvará" },
    { id: "listar-alvaras", label: "Listar Alvarás" },
    { id: "administracao", label: "Administração" },
    { id: "relatorios", label: "Relatórios" },
  ]

  const currentLabel = allTabs.find(t => t.id === activeTab)?.label || "Dashboard"

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard": return <DashboardHome userRole={userRole} userName={userName} onNavigate={setActiveTab} />
      case "nova-guia": return <NovaGuia editingGuiaId={editingGuiaId} onSaveComplete={handleSaveComplete} />
      case "listar-guias": return <ListarGuias onEdit={handleEditGuia} />
      case "lixeira-guias": return <LixeiraGuias />
      case "cadastrar-cemiterio": return <CadastrarCemiterio />
      case "listar-cemiterios": return <ListarCemiterios />
      case "cadastro-geral": return <CadastroGeral editingId={editingCadastroGeralId} onSaveComplete={handleSaveCadastroGeralComplete} />
      case "listar-cadastro-geral": return <ListarCadastroGeral onEdit={handleEditCadastroGeral} />
      case "gerar-pagamentos": return <GerarPagamentos />
      case "pagamentos-gerados": return <PagamentosGerados />
      case "pagamentos-confirmados": return <PagamentosConfirmados />
      case "pagamentos-expirados": return <PagamentosExpirados />
      case "aforamento": return <Aforamento editingAforamentoId={editingAforamentoId} onSaveComplete={handleSaveAforamentoComplete} />
      case "listar-aforamentos": return <ListarAforamentos onEdit={handleEditAforamento} onView={handleViewAforamento} />
      case "cadastrar-feirante": return <CadastrarFeirante editingFeiranteId={editingFeiranteId} onSaveComplete={handleSaveFeiranteComplete} />
      case "listar-feirantes": return <ListarFeirantes onEdit={handleEditFeirante} />
      case "cadastrar-vendedor": return <CadastrarVendedor editingVendedorId={editingVendedorId} onSaveComplete={handleSaveVendedorComplete} />
      case "listar-vendedores": return <ListarVendedores onEdit={handleEditVendedor} />
      case "cadastrar-boleto": return <CadastrarBoleto editingBoletoId={editingBoletoId} onSaveComplete={handleSaveBoletoComplete} />
      case "listar-boletos": return <ListarBoletos onEdit={handleEditBoleto} />
      case "cadastrar-empresa": return <CadastrarEmpresa editingId={editingEmpresaId} onSaveComplete={handleSaveEmpresaComplete} />
      case "listar-empresas": return <ListarEmpresas onEdit={handleEditEmpresa} onEmitirAlvara={handleEmitirAlvara} />
      case "emitir-alvara": return <EmitirAlvara empresaId={emitindoAlvaraEmpresaId} editingAlvaraId={editingAlvaraId} onSaveComplete={handleSaveAlvaraComplete} />
      case "listar-alvaras": return <ListarAlvaras onEdit={handleEditAlvara} />
      case "administracao": return <Administracao />
      case "relatorios": return <Relatorios />
      default: return <DashboardHome userRole={userRole} userName={userName} onNavigate={setActiveTab} />
    }
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">

      {/* ── BACKDROP mobile ── */}
      <AnimatePresence>
        {sidebarOpen && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 bg-black/50 z-40"
          />
        )}
      </AnimatePresence>

      {/* ── SIDEBAR ── */}
      <AnimatePresence initial={false}>
        {(!isMobile || sidebarOpen) && (
          <motion.aside
            key="sidebar"
            initial={isMobile ? { x: -300 } : { x: 0 }}
            animate={{ x: 0 }}
            exit={isMobile ? { x: -300 } : { x: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className={`
              bg-[#004161] text-white shadow-2xl flex flex-col no-print
              ${isMobile ? "fixed top-0 left-0 h-full z-50 w-72" : "relative w-72 flex-shrink-0"}
            `}
          >
            {/* Logo + Título */}
            <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-3 min-w-0">
                <img src={logoUrl} alt="Logo" className="h-10 w-auto flex-shrink-0" />
                <div className="min-w-0">
                  <h2 className="font-bold text-base leading-tight truncate">Sistema EMIT</h2>
                  <p className="text-xs text-blue-200 truncate">Sepultamento Municipal</p>
                </div>
              </div>
              {isMobile && (
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0 ml-2"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Navegação */}
            <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
              {menuGroups.map((group) => {
                if (group.adminOnly && userRole !== "admin") return null
                const Icon = group.icon
                const hasItems = !!(group.items && group.items.length > 0)
                const isGroupOpen = openDropdown === group.id
                const isActive = hasItems
                  ? group.items?.some(item => item.id === activeTab)
                  : activeTab === group.tab

                if (!hasItems) {
                  return (
                    <motion.button
                      key={group.id}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => group.tab && handleNavigate(group.tab)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-medium ${
                        isActive ? "bg-white/20 text-white" : "hover:bg-white/10 text-blue-100"
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{group.label}</span>
                    </motion.button>
                  )
                }

                return (
                  <div key={group.id}>
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={() => toggleDropdown(group.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-medium ${
                        isActive ? "bg-white/20 text-white" : "hover:bg-white/10 text-blue-100"
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="flex-1 text-left truncate">{group.label}</span>
                      <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isGroupOpen ? "rotate-180" : ""}`} />
                    </motion.button>

                    <AnimatePresence>
                      {isGroupOpen && group.items && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="ml-3 mt-0.5 space-y-0.5 overflow-hidden border-l border-white/10 pl-3"
                        >
                          {group.items.map((item) => {
                            const ItemIcon = item.icon
                            const isItemActive = activeTab === item.id
                            return (
                              <motion.button
                                key={item.id}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => handleNavigate(item.id)}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm ${
                                  isItemActive ? "bg-white/15 text-white font-medium" : "hover:bg-white/8 text-blue-100"
                                }`}
                              >
                                <ItemIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="truncate">{item.label}</span>
                              </motion.button>
                            )
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              })}
            </nav>

            {/* Botão Sair */}
            <div className="px-3 py-4 border-t border-white/10">
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={onLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-red-500/20 transition-all border border-red-400/30 text-sm font-medium"
              >
                <LogOut className="w-4 h-4 flex-shrink-0" />
                <span>Sair do Sistema</span>
              </motion.button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* ── CONTEÚDO PRINCIPAL ── */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 no-print flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Hamburguer mobile */}
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0"
              >
                <Menu className="w-5 h-5 text-[#004161]" />
              </button>
            )}

            {/* Logo mobile no header */}
            {isMobile && (
              <img src={logoUrl} alt="Logo" className="h-8 w-auto flex-shrink-0" />
            )}

            <div className="flex-1 min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-gray-800 truncate leading-tight">
                {currentLabel}
              </h1>
              {!isMobile && (
                <p className="text-xs text-gray-400 mt-0.5">Sistema EMIT — Sepultamento Municipal</p>
              )}
            </div>

            {/* Avatar + nome */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isMobile && (
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-700 leading-tight">{userName}</p>
                  <p className="text-xs text-gray-400">{userRole === "admin" ? "Administrador" : "Usuário"}</p>
                </div>
              )}
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-[#004161] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {userName.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Área de conteúdo */}
        <div className={`flex-1 overflow-auto ${isMobile ? "p-3 pb-20" : "p-6"}`}>
          {renderContent()}
        </div>

        {/* Rodapé desktop */}
        {!isMobile && (
          <footer className="bg-white border-t border-gray-200 py-3 px-6 no-print flex-shrink-0">
            <p className="text-center text-gray-400 text-xs">
              Desenvolvido por <span className="font-semibold text-[#004161]">Railson Abreu</span>
            </p>
          </footer>
        )}
      </main>

      {/* ── BARRA INFERIOR MOBILE (estilo app) ── */}
      {isMobile && (
        <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 no-print safe-bottom">
          <div className="flex items-stretch h-16">
            {bottomNav.map((item) => {
              const Icon = item.icon
              const isMenuBtn = (item.tab as string) === "menu"
              const isActive = !isMenuBtn && activeTab === item.tab

              return (
                <button
                  key={item.tab}
                  onClick={() => {
                    if (isMenuBtn) {
                      setSidebarOpen(true)
                    } else {
                      handleNavigate(item.tab)
                    }
                  }}
                  className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors ${
                    isActive
                      ? "text-[#004161]"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  <div className={`p-1 rounded-lg transition-colors ${isActive ? "bg-[#004161]/10" : ""}`}>
                    <Icon className={`w-5 h-5 ${isActive ? "text-[#004161]" : ""}`} />
                  </div>
                  <span className={`text-[10px] font-medium leading-none ${isActive ? "text-[#004161]" : ""}`}>
                    {item.label}
                  </span>
                </button>
              )
            })}
          </div>
        </nav>
      )}

    </div>
  )
}
